import React, { useState, useEffect } from "react";
import {
  Chip,
  IconButton,
  Stack,
  Typography,
  Box,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Alert,
  Snackbar,
  Collapse,
  Button,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DragIndicator as DragIcon,
  Layers as LayersIcon,
  Block as BlockIcon,
  Factory as FactoryIcon,
  Preview as PreviewIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import EditIconSvg from "@/assets/svgs/edit.svg";
import DeleteIconSvg from "@/assets/svgs/garbage.svg";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { 
  DutyColumn, 
  useUpdateDutyColumnMutation,
  DutyColumnUpdateInput,
} from "@/services/product-catalogue";
import DutyStackingPreview from "../duty-stacking-preview";
import { tokens } from "@/tokens";

interface DutyColumnsTableProps {
  data: DutyColumn[];
  onEditColumn: (column: DutyColumn) => void;
  onDeleteColumn: (columnId: string) => void;
  onToggleActive: (columnId: string, isActive: boolean) => void;
  globalOrderIds?: string[];
}

interface SortableRowProps {
  column: DutyColumn;
  onEditColumn: (column: DutyColumn) => void;
  onDeleteColumn: (columnId: string) => void;
  onToggleActive: (columnId: string, isActive: boolean) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  column,
  onEditColumn,
  onDeleteColumn,
  onToggleActive,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 1,
  };


  const hasMetalRules = column.rules?.some(rule => rule.isMetal) || false;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover': {
          backgroundColor: tokens.bg.hover,
        },
      }}
    >
      {/* Drag Handle */}
      <TableCell sx={{ width: '40px', padding: '8px' }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIcon sx={{ color: tokens.text.muted, fontSize: '20px' }} />
        </Box>
      </TableCell>

      {/* Column Name with Metal Indicator */}
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
            {column.name}
          </Typography>
          {hasMetalRules && (
            <Tooltip title="Contains metal percentage rules">
              <FactoryIcon sx={{ fontSize: 16, color: tokens.status.warning }} />
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      {/* Status */}
      <TableCell>
        <Chip
          size="small"
          label={column.isActive ? 'Active' : 'Inactive'}
          color={column.isActive ? 'success' : 'default'}
          sx={{
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.base,
          }}
        />
      </TableCell>

      {/* Priority */}
      <TableCell align="center">
        <Chip
          size="small"
          label={column.columnPriority || 10}
          color="primary"
          variant="outlined"
          sx={{
            fontWeight: tokens.fontWeight.bold,
            fontSize: tokens.fontSize.base,
            minWidth: '40px',
          }}
        />
      </TableCell>

      {/* Stacking */}
      <TableCell align="center">
        {column.isStackable !== undefined ? (
          column.isStackable ? (
            <Tooltip title="Rates stack with other columns">
              <Chip
                icon={<LayersIcon sx={{ fontSize: 16 }} />}
                size="small"
                label="Stackable"
                color="primary"
                sx={{ fontSize: tokens.fontSize.xs }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Rate only applies if no other rate exists">
              <Chip
                size="small"
                label="Exclusive"
                sx={{ fontSize: tokens.fontSize.xs }}
              />
            </Tooltip>
          )
        ) : (
          <Tooltip title="Defaults to stackable">
            <Chip
              icon={<LayersIcon sx={{ fontSize: 16 }} />}
              size="small"
              label="Stackable"
              color="primary"
              sx={{ fontSize: tokens.fontSize.xs }}
            />
          </Tooltip>
        )}
      </TableCell>

      {/* Mutual Exclusion Group */}
      <TableCell>
        {column.mutuallyExclusiveGroup ? (
          <Tooltip title={`Part of exclusion group: ${column.mutuallyExclusiveGroup}`}>
            <Chip
              icon={<BlockIcon sx={{ fontSize: 16 }} />}
              size="small"
              label={column.mutuallyExclusiveGroup}
              color="error"
              variant="outlined"
              sx={{
                fontSize: tokens.fontSize.xs,
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
          </Tooltip>
        ) : (
          <Typography variant="body2" color={tokens.text.muted} fontSize={tokens.fontSize.base}>
            None
          </Typography>
        )}
      </TableCell>


      {/* Actions */}
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title={column.isActive ? "Deactivate" : "Activate"}>
            <IconButton
              size="small"
              onClick={() => onToggleActive(column._id, !column.isActive)}
              sx={{
                color: column.isActive ? '#f59e0b' : '#10b981',
                '&:hover': {
                  backgroundColor: column.isActive ? tokens.status.warningBg : tokens.chip.green.bg,
                },
              }}
            >
              {column.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEditColumn(column)}
              sx={{
                color: '#3b82f6',
                '&:hover': {
                  backgroundColor: tokens.status.infoBg,
                },
              }}
            >
              <img src={EditIconSvg} alt="Edit" style={{ width: 24, height: 24 }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDeleteColumn(column._id)}
              sx={{
                color: tokens.status.danger,
                '&:hover': {
                  backgroundColor: tokens.status.dangerBg,
                },
              }}
            >
              <img src={DeleteIconSvg} alt="Delete" style={{ width: 24, height: 24 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const DutyColumnsTable: React.FC<DutyColumnsTableProps> = ({
  data,
  onEditColumn,
  onDeleteColumn,
  onToggleActive,
  globalOrderIds,
}) => {
  const [columns, setColumns] = useState<DutyColumn[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [updateDutyColumn] = useUpdateDutyColumnMutation();

  useEffect(() => {
    // Sort initial data by displayOrder, falling back to columnPriority for stablesort
    const sortedData = [...data].sort((a, b) => {
      const displayOrderA = a.displayOrder ?? Infinity;
      const displayOrderB = b.displayOrder ?? Infinity;
      if (displayOrderA !== displayOrderB) {
        return displayOrderA - displayOrderB;
      }
      return (a.columnPriority || 10) - (b.columnPriority || 10);
    });
    setColumns(sortedData);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = columns.findIndex((c) => c._id === active.id);
      const newIndex = columns.findIndex((c) => c._id === over!.id);
      const newOrder = arrayMove(columns, oldIndex, newIndex);

      const originalOrder = [...columns]; // Save original order for rollback
      setColumns(newOrder); // Optimistic update

      // Compute global ordering update
      const visibleIds = originalOrder.map(c => c._id);
      const reorderedVisibleIds = newOrder.map(c => c._id);
      const currentGlobalIds = (globalOrderIds && globalOrderIds.length > 0)
        ? [...globalOrderIds]
        : [...visibleIds];

      // Replace occurrences of visible ids in the global list with the new visible sequence
      const visibleSet = new Set(visibleIds);
      const newGlobalIds: string[] = [];
      let nextVisibleIdx = 0;
      for (const id of currentGlobalIds) {
        if (visibleSet.has(id)) {
          newGlobalIds.push(reorderedVisibleIds[nextVisibleIdx]);
          nextVisibleIdx += 1;
        } else {
          newGlobalIds.push(id);
        }
      }

      // Build minimal update payloads only for changed displayOrder
      const originalIndexById = new Map<string, number>(
        currentGlobalIds.map((id, idx) => [id, idx + 1])
      );

      const updates = newGlobalIds
        .map((id, idx) => ({ id, displayOrder: idx + 1 }))
        .filter(({ id, displayOrder }) => originalIndexById.get(id) !== displayOrder)
        .map(({ id, displayOrder }) => ({ id, payload: { displayOrder } as DutyColumnUpdateInput }));

      setSnackbar({ open: true, message: 'Updating column order...', severity: 'success' });

      try {
        await Promise.all(
          updates.map(u => updateDutyColumn({ id: u.id, dutyColumn: u.payload }).unwrap())
        );
        setSnackbar({ open: true, message: 'Column order updated successfully!', severity: 'success' });
      } catch (error) {
        console.error('Failed to update column order:', error);
        setSnackbar({ open: true, message: 'Failed to update order. Reverting changes.', severity: 'error' });
        setColumns(originalOrder); // Revert on failure
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(null);
  };

  if (!data || data.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No duty columns found. Create a new one to get started.
      </Alert>
    );
  }

  return (
    <Box>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity || 'info'} onClose={handleCloseSnackbar}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
      
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
            Drag and drop to reorder columns. This changes the **Display Order**.
          </Typography>
          <Button
            startIcon={previewOpen ? <ExpandLessIcon /> : <PreviewIcon />}
            onClick={() => setPreviewOpen(!previewOpen)}
            size="small"
            variant="outlined"
          >
            {previewOpen ? 'Hide' : 'Show'} Stacking Preview
          </Button>
        </Stack>
        
        <Collapse in={previewOpen}>
          <DutyStackingPreview columns={columns} />
        </Collapse>
      </Stack>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${tokens.border.default}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: tokens.bg.hover }}>
                <TableCell sx={{ width: '40px' }}></TableCell>
                <TableCell>Column Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Priority</TableCell>
                <TableCell align="center">Stacking</TableCell>
                <TableCell>Exclusion Group</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext 
                items={columns.map(item => item._id)} 
                strategy={verticalListSortingStrategy}
              >
                {columns.map((column) => (
                  <SortableRow
                    key={column._id}
                    column={column}
                    onEditColumn={onEditColumn}
                    onDeleteColumn={onDeleteColumn}
                    onToggleActive={onToggleActive}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
    </Box>
  );
};

export default DutyColumnsTable; 